import { devLog, devError } from '../utils/devLogger';
import { isRealUser } from '../utils/userFilters';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import QANavbar from '../components/QANavbar';
import '../styles/brand.css';

// Roles allowed to access progress reports
const ALLOWED_ROLES = ['admin', 'facilitator', 'qa'];

const ProgressReports = () => {
  const { currentUser, userProfile } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCohort, setFilterCohort] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [cohorts, setCohorts] = useState([]);

  const hasAccess = ALLOWED_ROLES.includes(userProfile?.role);

  useEffect(() => {
    if (currentUser && hasAccess) {
      loadData();
    }
  }, [currentUser, userProfile, hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load users
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersData = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(isRealUser);
      
      // Load modules
      const modulesCollection = collection(db, 'modules');
      const modulesSnapshot = await getDocs(modulesCollection);
      const modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Load enrollments
      const enrollmentsCollection = collection(db, 'enrollments');
      const enrollmentsSnapshot = await getDocs(enrollmentsCollection);
      const enrollmentsData = enrollmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt || Date.now())
      }));
      
      // Load cohorts
      const cohortsCollection = collection(db, 'cohorts');
      const cohortsSnapshot = await getDocs(cohortsCollection);
      const cohortsData = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setCohorts(cohortsData);
      
      // Generate progress reports
      generateProgressReports(usersData, modulesData, enrollmentsData);
      
    } catch (error) {
      devError('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateProgressReports = (usersData, modulesData, enrollmentsData) => {
    const learners = usersData.filter(user => user.role === 'learner');
    
    const progressReports = learners.map(learner => {
      const learnerEnrollments = enrollmentsData.filter(e => e.userId === learner.id);
      const completedEnrollments = learnerEnrollments.filter(e => e.completed === true);
      const inProgressEnrollments = learnerEnrollments.filter(e => e.progress > 0 && !e.completed);
      
      const totalModules = learnerEnrollments.length;
      const completedModules = completedEnrollments.length;
      const overallProgress = totalModules > 0 ? (completedModules / totalModules) * 100 : 0;
      
      // Calculate average score
      const gradesWithScores = completedEnrollments.filter(e => e.finalScore !== undefined);
      const avgScore = gradesWithScores.length > 0 
        ? gradesWithScores.reduce((sum, e) => sum + e.finalScore, 0) / gradesWithScores.length 
        : 0;
      
      // Calculate time to completion
      const completedWithDates = completedEnrollments.filter(e => e.completedAt && e.startedAt);
      const avgCompletionTime = completedWithDates.length > 0
        ? completedWithDates.reduce((sum, e) => {
            const startDate = e.startedAt?.toDate?.() || new Date(e.startedAt);
            const endDate = e.completedAt?.toDate?.() || new Date(e.completedAt);
            return sum + (endDate - startDate);
          }, 0) / completedWithDates.length
        : 0;
      
      return {
        learner,
        totalModules,
        completedModules,
        inProgressModules: inProgressEnrollments.length,
        overallProgress: Math.round(overallProgress),
        avgScore: Math.round(avgScore),
        avgCompletionDays: Math.round(avgCompletionTime / (1000 * 60 * 60 * 24)),
        status: getProgressStatus(overallProgress, inProgressEnrollments.length),
        lastActivity: getLastActivity(learnerEnrollments)
      };
    });
    
    setReports(progressReports);
  };

  const getProgressStatus = (progress, inProgressCount) => {
    if (progress === 100) return 'completed';
    if (progress > 50 || inProgressCount > 0) return 'active';
    if (progress > 0) return 'started';
    return 'not_started';
  };

  const getLastActivity = (enrollments) => {
    const activitiesWithDates = enrollments.filter(e => e.lastAccessedAt);
    if (activitiesWithDates.length === 0) return null;
    
    const latestActivity = activitiesWithDates.reduce((latest, current) => {
      const currentDate = current.lastAccessedAt?.toDate?.() || new Date(current.lastAccessedAt);
      const latestDate = latest?.toDate?.() || new Date(latest || 0);
      return currentDate > latestDate ? current.lastAccessedAt : latest;
    }, null);
    
    return latestActivity?.toDate?.() || new Date(latestActivity);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'completed': 'success',
      'active': 'primary',
      'started': 'warning',
      'not_started': 'secondary'
    };
    const labels = {
      'completed': 'Completed',
      'active': 'Active',
      'started': 'Started',
      'not_started': 'Not Started'
    };
    return <Badge bg={variants[status]}>{labels[status]}</Badge>;
  };

  const getCohortName = (cohortId) => {
    const cohort = cohorts.find(c => c.id === cohortId);
    return cohort ? cohort.name : 'No Cohort';
  };

  const filteredReports = reports.filter(report => {
    const cohortMatch = filterCohort === 'all' || report.learner.cohortId === filterCohort;
    const statusMatch = filterStatus === 'all' || report.status === filterStatus;
    return cohortMatch && statusMatch;
  });

  // Render appropriate navbar based on role
  const renderNavbar = () => {
    switch (userProfile?.role) {
      case 'admin':
        return <AdminNavbar currentPage="progress-reports" />;
      case 'qa':
        return <QANavbar />;
      case 'facilitator':
        return <FacilitatorNavbar currentPage="progress-reports" />;
      default:
        return <AdminNavbar currentPage="progress-reports" />;
    }
  };

  if (!hasAccess) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access progress reports.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        {renderNavbar()}
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Generating progress reports...</p>
          </div>
        </Container>
      </>
    );
  }

  const overallStats = {
    totalLearners: reports.length,
    completedLearners: reports.filter(r => r.status === 'completed').length,
    activeLearners: reports.filter(r => r.status === 'active').length,
    avgProgress: reports.length > 0 ? Math.round(reports.reduce((sum, r) => sum + r.overallProgress, 0) / reports.length) : 0
  };

  return (
    <>
      {renderNavbar()}

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Progress Reports</h1>
              <p className="mission-text mb-4">
                Track learner progress and performance across programs
              </p>
              <p className="mb-0">
                Monitor individual learner progress, module completions, and overall
                performance metrics with detailed analytics and reporting capabilities.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button variant="outline-light">
                Export Reports
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {/* Overall Statistics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{overallStats.totalLearners}</h3>
                <small className="text-muted">Total Learners</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{overallStats.completedLearners}</h3>
                <small className="text-muted">Completed Programs</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-info">{overallStats.activeLearners}</h3>
                <small className="text-muted">Active Learners</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">{overallStats.avgProgress}%</h3>
                <small className="text-muted">Average Progress</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Filter by Cohort</Form.Label>
                      <Form.Select 
                        value={filterCohort} 
                        onChange={(e) => setFilterCohort(e.target.value)}
                      >
                        <option value="all">All Cohorts</option>
                        {cohorts.map(cohort => (
                          <option key={cohort.id} value={cohort.id}>{cohort.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Filter by Status</Form.Label>
                      <Form.Select 
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">All Statuses</option>
                        <option value="completed">Completed</option>
                        <option value="active">Active</option>
                        <option value="started">Started</option>
                        <option value="not_started">Not Started</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Progress Reports Table */}
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5>Learner Progress Reports</h5>
              <small className="text-muted">Showing {filteredReports.length} of {reports.length} learners</small>
            </div>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Cohort</th>
                  <th>Progress</th>
                  <th>Modules</th>
                  <th>Avg Score</th>
                  <th>Avg Completion Time</th>
                  <th>Status</th>
                  <th>Last Activity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.learner.id}>
                    <td>
                      <div>
                        <strong>{report.learner.displayName}</strong>
                        <div><small className="text-muted">{report.learner.email}</small></div>
                      </div>
                    </td>
                    <td>{getCohortName(report.learner.cohortId)}</td>
                    <td>
                      <div className="mb-1">
                        <small>{report.overallProgress}%</small>
                      </div>
                      <ProgressBar 
                        now={report.overallProgress} 
                        variant={report.overallProgress > 80 ? 'success' : report.overallProgress > 50 ? 'warning' : 'danger'}
                        size="sm"
                      />
                    </td>
                    <td>
                      <div>
                        <Badge bg="success">{report.completedModules}</Badge>
                        <span className="mx-1">/</span>
                        <Badge bg="secondary">{report.totalModules}</Badge>
                      </div>
                      {report.inProgressModules > 0 && (
                        <div><small className="text-info">{report.inProgressModules} in progress</small></div>
                      )}
                    </td>
                    <td>
                      {report.avgScore > 0 ? (
                        <Badge bg={report.avgScore >= 80 ? 'success' : report.avgScore >= 60 ? 'warning' : 'danger'}>
                          {report.avgScore}%
                        </Badge>
                      ) : (
                        <small className="text-muted">No scores</small>
                      )}
                    </td>
                    <td>
                      {report.avgCompletionDays > 0 ? (
                        <span>{report.avgCompletionDays} days</span>
                      ) : (
                        <small className="text-muted">N/A</small>
                      )}
                    </td>
                    <td>{getStatusBadge(report.status)}</td>
                    <td>
                      {report.lastActivity ? (
                        <small>{report.lastActivity.toLocaleDateString()}</small>
                      ) : (
                        <small className="text-muted">No activity</small>
                      )}
                    </td>
                    <td>
                      <Button variant="outline-primary" size="sm">
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredReports.length === 0 && (
                  <tr>
                    <td colSpan="9" className="text-center text-muted py-4">
                      No progress reports found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ProgressReports;